import React, { useState } from "react";
import { ArrowRight, Sparkles, Mail, Lock as LockIcon, User as UserIcon, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";

const formatDetail = (detail) => {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  return String(detail);
};

const Login = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  // shared form state (email/password used by both tabs)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // signup extras (all required)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [cap, setCap] = useState("");
  const [city, setCity] = useState("");
  // Legal consent — mandatory at signup (Feb 2026)
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (endpoint) => {
    if (!email.trim() || !password.trim()) { toast.error("Compila email e password"); return; }
    if (endpoint === "/auth/register") {
      if (password.length < 8) { toast.error("Password minima 8 caratteri"); return; }
      for (const [v, lbl] of [[firstName, "Nome"], [lastName, "Cognome"], [phone, "Cellulare"], [address, "Indirizzo"], [cap, "CAP"], [city, "Città"]]) {
        if (!v.trim()) { toast.error(`Campo obbligatorio: ${lbl}`); return; }
      }
      if (!acceptTerms) { toast.error("Devi accettare i Termini di Servizio"); return; }
      if (!acceptPrivacy) { toast.error("Devi accettare l'Informativa Privacy"); return; }
    }
    setBusy(true);
    try {
      const payload = endpoint === "/auth/register"
        ? {
            email: email.trim(), password,
            first_name: firstName.trim(), last_name: lastName.trim(),
            phone: phone.trim(), address: address.trim(),
            cap: cap.trim(), city: city.trim(),
            name: `${firstName.trim()} ${lastName.trim()}`.trim(),
            accepted_terms: true,
            accepted_privacy: true,
          }
        : { email: email.trim(), password };
      const r = await api.post(endpoint, payload);
      setUser(r.data.user);
      toast.success(endpoint === "/auth/register" ? "Benvenuto in ImmoMap!" : "Bentornato!");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Errore di autenticazione");
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    submit(tab === "signup" ? "/auth/register" : "/auth/login");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FAFAF7]">
      <div className="flex flex-col justify-between p-8 lg:p-14 relative">
        <div className="flex items-center gap-3">
          <img src="/immomap-logo.png" alt="ImmoMap" className="w-14 h-14 object-contain" />
          <div>
            <div className="font-display font-bold text-2xl tracking-tight leading-none" style={{ color: "#12466F" }}>ImmoMap</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mt-1">Real Estate Mapping</div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto lg:mx-0">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-slate-500 mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Piattaforma di mappatura
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[0.98] text-slate-900">
            Mappa il mercato.<br/>
            <span style={{ color: "#12466F" }}>Collabora</span> sulle schede.
          </h1>

          {/* Google button — always visible on top */}
          <Button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="mt-8 w-full h-11 rounded-full bg-[#12466F] hover:bg-[#0b3557] text-white font-semibold flex items-center justify-center gap-3 group shadow-lg shadow-blue-900/20"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span>Continua con Google</span>
            <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-1 transition-transform" />
          </Button>

          {/* Divider */}
          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">oppure</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Tabs value={tab} onValueChange={setTab} className="mt-5">
            <TabsList className="w-full grid grid-cols-2 bg-slate-100 rounded-full p-1 h-11">
              <TabsTrigger value="login" data-testid="tab-login" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">Accedi</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">Registrati</TabsTrigger>
            </TabsList>

            <form onSubmit={onSubmit} className="mt-4 space-y-3">
              <TabsContent value="signup" className="mt-0 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px] uppercase tracking-widest text-slate-500">Nome *</Label>
                    <Input data-testid="signup-firstname" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Mario" className="rounded-xl bg-white border border-slate-200 h-11 focus-visible:ring-2 focus-visible:ring-[#12466F] mt-1" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-widest text-slate-500">Cognome *</Label>
                    <Input data-testid="signup-lastname" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Rossi" className="rounded-xl bg-white border border-slate-200 h-11 focus-visible:ring-2 focus-visible:ring-[#12466F] mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-slate-500">Cellulare *</Label>
                  <Input data-testid="signup-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 333 1234567" className="rounded-xl bg-white border border-slate-200 h-11 focus-visible:ring-2 focus-visible:ring-[#12466F] mt-1" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-slate-500">Indirizzo *</Label>
                  <Input data-testid="signup-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Via Roma 12" className="rounded-xl bg-white border border-slate-200 h-11 focus-visible:ring-2 focus-visible:ring-[#12466F] mt-1" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[11px] uppercase tracking-widest text-slate-500">CAP *</Label>
                    <Input data-testid="signup-cap" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="00100" className="rounded-xl bg-white border border-slate-200 h-11 focus-visible:ring-2 focus-visible:ring-[#12466F] mt-1 font-mono-num" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[11px] uppercase tracking-widest text-slate-500">Città *</Label>
                    <Input data-testid="signup-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Roma" className="rounded-xl bg-white border border-slate-200 h-11 focus-visible:ring-2 focus-visible:ring-[#12466F] mt-1" />
                  </div>
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 mt-1">
                  <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#0A84FF]" data-testid="signup-accept-terms" />
                  <span className="text-[12px] text-slate-700 leading-snug">
                    Accetto i <Link to="/terms" target="_blank" className="text-[#0A84FF] underline">Termini di Servizio</Link> di ImmoMap.
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                  <input type="checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#0A84FF]" data-testid="signup-accept-privacy" />
                  <span className="text-[12px] text-slate-700 leading-snug">
                    Accetto l'<Link to="/terms" target="_blank" className="text-[#0A84FF] underline">Informativa Privacy</Link> e autorizzo il trattamento dei miei dati personali secondo il GDPR.
                  </span>
                </label>
              </TabsContent>

              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500">Email</Label>
                <div className="relative mt-1">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid={tab === "signup" ? "signup-email" : "login-email"}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@esempio.com"
                    autoComplete={tab === "signup" ? "email" : "username"}
                    required
                    className="rounded-xl bg-white border border-slate-200 pl-9 h-11 focus-visible:ring-2 focus-visible:ring-[#12466F]"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500">
                  Password {tab === "signup" && <span className="text-slate-400 normal-case tracking-normal">— min. 8 caratteri</span>}
                </Label>
                <div className="relative mt-1">
                  <LockIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid={tab === "signup" ? "signup-password" : "login-password"}
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete={tab === "signup" ? "new-password" : "current-password"}
                    required
                    minLength={tab === "signup" ? 8 : 1}
                    className="rounded-xl bg-white border border-slate-200 pl-9 pr-10 h-11 focus-visible:ring-2 focus-visible:ring-[#12466F]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPw ? "Nascondi password" : "Mostra password"}
                    data-testid="toggle-password"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid={tab === "signup" ? "signup-submit" : "login-submit"}
                disabled={busy}
                className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold mt-2"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Un attimo…</>
                ) : (
                  <>{tab === "signup" ? "Crea il mio account" : "Accedi"} <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </form>
          </Tabs>

          <p className="mt-5 text-xs text-slate-500 leading-relaxed">
            {tab === "signup"
              ? "Registrandoti crei un account Demo. Vedrai 6 immobili di esempio sulla mappa. Passa ad Abbonato per sbloccare tutte le funzioni."
              : (<>Non hai un account? Passa alla scheda <b>Registrati</b> o continua con Google.<br/><a href="/forgot-password" className="text-[#0A84FF] hover:underline mt-1 inline-block" data-testid="link-forgot">Password dimenticata?</a></>)
            }
          </p>
        </div>

        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">
          © {new Date().getFullYear()} ImmoMap — Real Estate Intelligence · <Link to="/terms" className="text-slate-500 hover:text-slate-700 underline" data-testid="footer-terms">Termini e Privacy</Link>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden" style={{ background: "linear-gradient(135deg, #12466F 0%, #1a5f92 100%)" }}>
        <div className="absolute inset-0 bg-grid opacity-15" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-[420px] h-[420px] rounded-3xl overflow-hidden" data-testid="login-logo-wrap">
            <img src="/immomap-logo.png" alt="" className="w-full h-full object-contain opacity-90 filter drop-shadow-2xl" />
          </div>
        </div>
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.3em] opacity-70 mb-3">Dashboard analitica</div>
          <div className="font-display font-bold text-3xl leading-tight max-w-md">
            Ogni immobile, ogni annuncio, ogni collaboratore, in un'unica mappa.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
